import React from 'react';
import { Tooltip } from 'react-tooltip';
import { MarketStateSnapshotCalendarHeader } from './MarketStateSnapshotCalendarHeader';
import { classNames } from '../utils/classNames';

// A compact month calendar: one grid per month, one cell per day, in the anatomy of the rates
// calendar (the same weekday header, gray grid lines, the day number top-left, today in a blue
// circle, a day without data in gray). The caller gives each day of its range a tone (the Tailwind
// classes of the fill, for example a gradient from the top and a bar at the bottom), a mark for the
// top-right corner, an accessible label, and the HTML of its hover. A click on a day of the range
// calls onSelect with its ISO date. The selected day has a blue ring. MonthGrid knows nothing about
// the data behind the tones and the marks.
export type MonthGridDay = {
	// The ISO date, YYYY-MM-DD.
	date: string,
	// The accessible name of the cell.
	label: string,
	toneClassName?: string,
	mark?: React.ReactNode,
	hoverHtml?: string,
};

export type MonthGridMonth = {
	year: number,
	// 0 = January.
	month: number,
};

type Props = {
	months: MonthGridMonth[],
	days: MonthGridDay[],
	selected?: string,
	today?: string,
	onSelect?: (date: string) => void,
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COLUMNS: Record<number, string> = { 1: '', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' };

const isoOf = (year: number, month: number, day: number) =>
	`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const MonthGrid: React.FunctionComponent<Props> = ({ months, days, selected, today, onSelect }) => {
	const tooltipId = `month-grid-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const byDate = new Map(days.map(day => [day.date, day]));

	return <div className={classNames('grid grid-cols-1 gap-6', COLUMNS[Math.min(months.length, 3)] ?? '')}>
		{months.map(({ year, month }) => {
			const leading = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
			const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
			const trailing = (7 - ((leading + length) % 7)) % 7;
			return <div key={`${year}-${month}`}>
				<p className="mb-2 text-sm font-semibold text-gray-900">{MONTH_NAMES[month]} {year}</p>
				<div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
					<MarketStateSnapshotCalendarHeader />
					<div className="grid grid-cols-7 gap-px bg-gray-200 text-xs leading-6 text-gray-700">
						{Array.from({ length: leading }, (_, i) => <div key={`lead-${i}`} className="h-12 bg-gray-50" />)}
						{Array.from({ length }, (_, i) => {
							const date = isoOf(year, month, i + 1);
							const day = byDate.get(date);
							const isToday = date === today;
							const number = <time dateTime={date}
								className={classNames(isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 font-semibold text-white' : '')}>{i + 1}</time>;
							if (day === undefined) {
								return <div key={date} className="h-12 bg-gray-50 px-2 py-1 text-gray-400">{number}</div>;
							}
							const isSelected = date === selected;
							return <button key={date} type="button"
								aria-label={day.label} aria-pressed={isSelected}
								data-tooltip-id={day.hoverHtml !== undefined ? tooltipId : undefined}
								data-tooltip-html={day.hoverHtml}
								onClick={() => { if (onSelect !== undefined) onSelect(date); }}
								className={classNames('relative h-12 bg-white text-left hover:bg-gray-50 focus:outline-none focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-sky-300',
									isSelected ? 'z-10' : '')}>
								<div className={classNames('flex h-full flex-col', day.toneClassName ?? '')}>
									<div className="flex items-start justify-between px-2 py-1">
										<span className={isSelected ? 'font-semibold text-gray-900' : ''}>{number}</span>
										{day.mark}
									</div>
								</div>
								{/* The ring is a layer above the tone: an inset ring on the button is under the tone. */}
								{isSelected && <span aria-hidden="true" className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-blue-600" />}
							</button>;
						})}
						{Array.from({ length: trailing }, (_, i) => <div key={`trail-${i}`} className="h-12 bg-gray-50" />)}
					</div>
				</div>
			</div>;
		})}
		<Tooltip id={tooltipId} place="top" positionStrategy="fixed" opacity={1} delayShow={80}
			className="z-50 !rounded-lg !bg-white !px-2.5 !py-2.5 !text-xs !text-gray-600 shadow ring-1 ring-gray-200" />
	</div>;
};

export default MonthGrid;
